import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { constants } from "@lib/constants";

// -------------------- Initial State --------------------
const initialState = {
  subscription: null,
  subscriptionLoading: false,
  subscriptionError: null,

  // NEW
  requiresPayment: false,
  order: null,
  transaction: null,

  transactions: null,
  transactionsLoading: false,
  transactionsError: null,
};

// -------------------- Thunks --------------------

// Fetch current subscription for user
export const fetchSubscription = createAsyncThunk(
  "subscription/fetchSubscription",
  async ({ axiosGet, userId }, thunkAPI) => {
    try {
      const response = await axiosGet(`${constants.BASE_URL}subscription/getSubscription/${userId}`);
      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// get List of Transactions
export const fetchTransactions = createAsyncThunk(
  "subscription/fetchTransactions",
  async ({ axiosGet, userId }, thunkAPI) => {
    try {
      const response = await axiosGet(`${constants.BASE_URL}subscription/transactions/${userId}`);
      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ------------------------------------------------------
// 🔵 NEW — UPDATE SUBSCRIPTION
// ------------------------------------------------------
export const updateSubscription = createAsyncThunk(
  "subscription/updateSubscription",
  async ({ axiosPost, userId, subscriptionType, subscriptionPlan }, thunkAPI) => {
    try {
      const res = await axiosPost(
        `${constants.BASE_URL}subscription/update/${userId}`,
        { subscriptionType, subscriptionPlan }
      );

      return res; // { requiresPayment, order, transaction, subscription }
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ------------------------------------------------------
// 🔵 NEW — VERIFY PAYMENT
// ------------------------------------------------------
export const verifyPayment = createAsyncThunk(
  "subscription/verifyPayment",
  async ({ axiosPost, payload }, thunkAPI) => {
    try {
      const res = await axiosPost(`${constants.BASE_URL}subscription/verify`, payload);
      return res; // { subscription }
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// -------------------- Slice --------------------
const subscriptionReducer = createSlice({
  name: "subscription",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ---------------- Fetch Subscription ----------------
      .addCase(fetchSubscription.pending, (state) => {
        state.subscriptionLoading = true;
        state.subscriptionError = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false;
        state.subscription = action.payload || null;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = action.payload;
      })

      // ---------------- Fetch Transactions ----------------
      .addCase(fetchTransactions.pending, (state) => {
        state.transactionsLoading = true;
        state.transactionsError = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false;
        state.transactions = action?.payload?.transactions || null;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.transactionsLoading = false;
        state.transactionsError = action.payload;
      })

      // ------------------------------------------------------
      // 🔵 UPDATE SUBSCRIPTION
      // ------------------------------------------------------
      .addCase(updateSubscription.pending, (state) => {
        state.subscriptionLoading = true;
        state.subscriptionError = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false;

        const { requiresPayment, order, transaction, subscription } = action.payload;

        state.requiresPayment = requiresPayment || false;
        state.order = order || null;
        state.transaction = transaction || null;

        if (!requiresPayment && subscription) {
          state.subscription = subscription;
        }
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = action.payload;
      })

      // ------------------------------------------------------
      // 🔵 VERIFY PAYMENT
      // ------------------------------------------------------
      .addCase(verifyPayment.pending, (state) => {
        state.subscriptionLoading = true;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.subscriptionLoading = false;

        state.subscription = action.payload.subscription;
        state.requiresPayment = false;
        state.order = null;
        state.transaction = null;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = action.payload;
      });
  },
});

export default subscriptionReducer.reducer;
